import { PredefinedFieldKey } from '@seek/cmsu-cms-connect';
import {
  isEmail,
  maxLength,
  minLength,
  minValue,
  maxValue,
} from '@seek/validators-js';

import type { FormField, ValidatorOptions } from './types';

type FormValidator =
  | {
      getErrorMessage: () => Promise<string>;
      test: ({ value }: { value: string }) => boolean;
    }
  | {
      getErrorMessage: () => Promise<string>;
      test: ({ value }: { value: number }) => boolean;
    }
  | {
      getErrorMessage: () => Promise<string>;
      test: ({ value }: { value: object }) => boolean;
    };

export const transformValueToValidator = (
  validator: ValidatorOptions,
  t: any,
): FormValidator => {
  switch (validator.__typename) {
    case 'MinLengthValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Min length', { value: validator.value }),
          ),
        test: ({ value }: { value: string }) =>
          minLength.test({
            value,
            validatorProps: { min: validator.value },
            formValues: undefined,
          }),
      };
    case 'MaxLengthValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Max length', { value: validator.value }),
          ),
        test: ({ value }: { value: string }) =>
          maxLength.test({
            value,
            validatorProps: { max: validator.value },
            formValues: undefined,
          }),
      };
    case 'MinNumberValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Min number', { value: validator.value }),
          ),
        test: ({ value }: { value: number }) =>
          minValue.test({
            value,
            validatorProps: {
              min: validator.value,
              minValueFieldLabel: '',
            },
            formValues: undefined,
          }),
      };
    case 'MaxNumberValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Max number', { value: validator.value }),
          ),
        test: ({ value }: { value: number }) =>
          maxValue.test({
            value,
            validatorProps: {
              max: validator.value,
              maxValueFieldLabel: '',
            },
            formValues: undefined,
          }),
      };
    case 'EmailValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(validator.errorMessage || t('Invalid email')),
        test: ({ value }: { value: string }) =>
          isEmail.test({
            value,
            formValues: undefined,
            validatorProps: undefined,
          }),
      };
    case 'MinCheckedItemValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Min checked item', { value: validator.value }),
          ),
        test: ({ value: obj }: { value: object }) => {
          const checkedCount = Object.values(obj).filter(
            (value) => value === true,
          ).length;
          return checkedCount >= validator.value;
        },
      };
    case 'MaxCheckedItemValidator':
      return {
        getErrorMessage: () =>
          Promise.resolve(
            validator.errorMessage ||
              t('Max checked item', { value: validator.value }),
          ),
        test: ({ value: obj }: { value: object }) => {
          const checkedCount = Object.values(obj).filter(
            (value) => value === true,
          ).length;
          return checkedCount <= validator.value;
        },
      };
  }
};

/**
 * Simple camel case, usually source is a string with space ('form label')
 * @param str
 * @returns
 */
export const toSimpleCamelCase = (str: string) => {
  const camelCase = str.replace(/[- ]+([a-zA-Z])/g, (_, letter) =>
    letter.toUpperCase(),
  );
  return camelCase.charAt(0).toLowerCase() + camelCase.slice(1);
};

/** All fields are somewhat valid because its going to fallback to the label because label is guaranteed to be present (required) */
export const getValidFieldKey = (field: FormField): string => {
  if (
    field.__typename === 'FormHiddenFieldGroup' ||
    field.__typename === 'FormFieldGroup'
  ) {
    return '';
  }

  if (
    field.predefinedKey &&
    field.predefinedKey !== PredefinedFieldKey.Custom
  ) {
    return field.predefinedKey;
  }

  if (field.key) {
    return field.key;
  }
  // checking if no predefined key or key is present
  // if so, we fallback to the label
  // currently different __typename has different label keys
  if (
    field.__typename === 'FormInputText' ||
    field.__typename === 'FormInputEmail' ||
    field.__typename === 'FormInputNumber'
  ) {
    return toSimpleCamelCase(field.inputLabel);
  }

  if (field.__typename === 'FormTextarea') {
    return toSimpleCamelCase(field.textareaLabel);
  }

  if (
    field.__typename === 'FormRadioGroup' ||
    field.__typename === 'FormCheckboxGroup'
  ) {
    return toSimpleCamelCase(field.label);
  }

  if (field.__typename === 'FormSelect') {
    return toSimpleCamelCase(field.selectLabel);
  }

  throw new Error('Invalid field configuration - no valid key found');
};
